import { addMonths, formatYmd } from "../../collection-schedule.js";
import { scrubPiiForLlm } from "../pii-scrubber.js";
import type { AiCapabilityPage } from "../registry/schema.js";
import { readMonthPhrase, scanDatePeriods, type DatePeriodPhrase, type MonthPhraseRead } from "../resolvers/date-slot.js";
import { hasNegationOrDoubt } from "../resolvers/enum-slot.js";
import { isUnaccentedText } from "../resolvers/name-text.js";
import { periodKeyLabelVi } from "../resolvers/named-ref-slot.js";
import { wordsOf } from "../resolvers/vi-words.js";
import { normalizeViText } from "../vi-normalizer.js";
import { collectionTermSpans, hasRecurringMarker, parsePerHeadCollection, type CollectionSlotFill } from "./collection-open.js";
import type { InterpreterSpanLike } from "./output.js";

/**
 * 🔴🔴 UX-COLLECTION-PERIOD (@UC0196 @UC0195 · nghiệm thu UX-H3 15/10/2026, LLM thật) — KỲ CỦA ĐỢT THU người dùng NÓI RÕ không được rơi lặng.
 *
 * ## Lỗi (đo, không đoán)
 * Chủ nhóm Tâm gõ *"thu quỹ tháng 11 mỗi người 150k"* ⇒ Người phiên dịch chọn `COL-EQUAL` (gạch `soTien`, không gạch gì cho "tháng 11" — trang
 * KHÔNG CÓ ô nào chứa được kỳ) ⇒ thẻ xác nhận: tên «Thu quỹ tháng bạn bấm Đồng ý», hạn «cuối tháng bạn bấm Đồng ý» (= 31/10, không phải 30/11). Đợt
 * đã mở thì app không đổi được hạn ⇒ sai không sửa được. `COL-EQUAL` là trang *"người mở KHÔNG nói hạn"* (máy quyết hạn — QĐ PO 20/09 A5 giữ lời hứa
 * không-cụ-thể vì phiếu và nút Đồng ý là hai lượt HTTP); câu nói một THÁNG cụ thể thì máy không được quyết hạn nữa.
 *
 * ## Sửa ở tầng tất định, 0 byte prompt (không đổi `llm.*`, không thêm ô ⇒ 0 ca eval mục)
 * Trang đã có sẵn cho "hạn do người dùng quyết" là `COL-DEADLINE` — ô `hanChot` (`date`) ĐÓNG BĂNG trên phiếu, thẻ in NGÀY CỤ THỂ, cùng bảng
 * `collections`, cùng luật phạm vi (`adhocAppliesToAllMembers`). Hai lưới, mỗi lưới một việc (luật chung 2 của `AI_DETERMINISTIC_NETS`):
 *  - `collectionPeriodPageFromSentence` (kind `page`) — trang đã chọn là trang đợt MỘT LẦN (`collectionFromSentence.cadence = "once"`) mà câu nói
 *    một tháng ⇒ chuyển sang trang `deadline` khai ô hạn (`collectionFromSentence.due`) trong `allowed`. Trang LỊCH (`recurring` — đo 15/10: LLM thật chọn
 *    COL-RECURRING 6/6 cho chính câu này) ⇒ chỉ chuyển khi tháng đọc được trọn VÀ câu không có chữ lặp. Trang khác ⇒ không đụng.
 *  - `fillCollectionPeriodFromSentence` (kind `slot`) — trên trang khai `due`: tháng đọc được ⇒ ô hạn = NGÀY CUỐI tháng đó, ô tên = "Thu quỹ tháng
 *    M/YYYY" (`collectionPeriodTitle` — CÙNG khuôn tên mặc định của `COL-EQUAL`); ô tiền Phiếu chưa gạch ⇒ chữ số "mỗi <người>" CHỈ khi câu thuộc khuôn
 *    đầu người (`parsePerHeadCollection`) — "tổng 3 triệu" không điền (trang HỎI). Chỉ ô Phiếu chưa gạch; không đè span model (trừ span hạn = đúng cụm
 *    tháng, `replacesSpan`). Khi chuyển trang, span Phiếu của ô cùng id + cùng loại được mang sang (`collectionPeriodCarriedSpans`).
 *
 * ## Đọc kỳ (`readCollectionPeriod`, hôm nay theo giờ VN do caller truyền — thuần)
 *  - Cụm tháng tìm bằng CHÍNH bảng khoảng ngày (`scanDatePeriods` + `readMonthPhrase` — một bảng khuôn tháng, không khuôn thứ hai): "tháng 11" ·
 *    "tháng 11/2027" · "tháng 11 năm 2027" · "tháng này/nay" · "tháng sau/tới" (có dấu hay cả câu không dấu — luật dấu `periodHeadOk`); cụm khoảng khác
 *    có chữ "tháng" ("đầu tháng") cũng là một lời về tháng ⇒ `unclear`.
 *  - Đọc ĐƯỢC (`month`) khi: đúng MỘT cụm tháng, câu không phủ định/nghi vấn, cụm đứng TRẦN (không chữ dẫn "từ/đến/hạn/trước", không là phần của một
 *    NGÀY "mùng 1 tháng sau" / "25 tháng này" / "đầu tháng sau" — "cuối/hết tháng N" vẫn là cuối tháng N), và tháng đó là tháng NÀY hoặc SAU: "tháng N"
 *    không năm với N ≥ tháng hiện tại ⇒ năm nay; có năm ⇒ không được trước tháng hiện tại; và phần câu còn lại KHÔNG nói một hạn/ngày nào ("hạn 5/11",
 *    "trước ngày 5", "deadline", "chậm nhất" — hạn khi đó là lời người dùng, không suy từ kỳ); KHÔNG cụm khoảng nào khác ("quý 4" — trừ "năm nay"), KHÔNG
 *    nhiều tháng viết tắt ("tháng 11, 12" · "11 và/đến 12" · "11-12"), KHÔNG chữ lặp/thu-tiếp ("hằng tháng", "trở đi", "đều đặn"…) — review #1432.
 *  - "năm sau" · "năm tới" · "sang năm" SÁT đúng một "tháng N" trần ("tháng 11 năm sau", "sang năm tháng 2") ⇒ tháng N của năm VN hiện tại + 1 — một năm
 *    CỤ THỂ, không đoán (UX-COLLECTION-EXTEND-YEAR M-3; `nextYearMarkers`). Lời năm sau ở chỗ khác / "năm sau nữa" / tháng đã có năm ⇒ `unclear`.
 *  - Còn lại (`unclear`): kỳ ĐÃ QUA ("tháng 9" lúc tháng 10 — hồi tháng 9, hay tháng 9 năm sau?), "tháng trước", ≥2 cụm, phủ định, cụm dính ngày/chữ
 *    dẫn ⇒ KHÔNG điền gì cho hạn/tên ⇒ trang hỏi «Hạn chót đóng là ngày nào?» (CLAUDE.md §4: không đoán). Vẫn CHUYỂN trang: ở `COL-EQUAL` kỳ đó bị bỏ lặng.
 *  - Không cụm tháng nào mà câu vẫn nói một KỲ (quý · năm · học kỳ · "năm học mới" · "sang năm" · "đầu năm" — `mentionsOtherTerm`, W-1 UX-COL-OPEN-PERIOD) ⇒
 *    `unclear` (các kỳ này chưa có luật hạn ⇒ HỎI, không về mặc định "tháng bạn bấm Đồng ý" — N-1: "thu quỹ lớp sang năm học mới mỗi bé 300k" 3/3 ra hạn
 *    cuối tháng NÀY). Không kỳ nào ⇒ `null` ⇒ đường cũ y nguyên, QĐ A5 ("tháng bạn bấm Đồng ý") giữ cho câu KHÔNG nói kỳ.
 *
 * ## Lệch luật chung có địa chỉ (ghi ở dòng `AI_DETERMINISTIC_NETS`, không giấu)
 *  - Luật 1 ("chỉ chữ của câu"): ô hạn nhận NGÀY "dd/mm/yyyy" và ô tên nhận "Thu quỹ tháng M/YYYY" do lưới DỰNG từ cụm tháng của câu + hôm nay —
 *    không phải nguyên chữ. Lý do: bộ đọc ngày cố ý không đọc "tháng 11" thành một ngày (review #1104: "hạn tháng sau" = ngày nào trong tháng ⇒ hỏi),
 *    còn ở đây NGHĨA đã rõ (kỳ đợt thu ⇒ hạn = hết kỳ, cùng luật "hạn = ngày cuối của kỳ" của `resolvePeriodDueDate`). Giá trị dựng ra vẫn đi qua
 *    resolver của ô và in trên thẻ xác nhận (AI-INV-4) — người dùng thấy NGÀY trước khi bấm. Năm/tháng chốt LÚC MỞ phiếu và đóng băng trong ô ⇒
 *    phiếu và lượt ghi đọc cùng một giá trị (bài học A5: không giá trị nào trên phiếu suy lại từ đồng hồ ở lượt bấm).
 *  - Luật 4 (phủ định ⇒ không làm gì): lưới trang vẫn chuyển (để HỎI hạn, như L07) — ở lại `COL-EQUAL` là im lặng ghi hạn máy tự chọn.
 */

type Ymd = { readonly y: number; readonly m: number; readonly d: number };

export type CollectionPeriod =
  | {
      readonly kind: "month";
      /** `YYYY-MM` (khớp `derivePeriodKey` tháng). */
      readonly periodKey: string;
      /** Ngày cuối tháng — `YYYY-MM-DD`. */
      readonly dueYmd: string;
      /** Ngày cuối tháng — `dd/mm/yyyy`, chữ đưa cho bộ đọc ô ngày. */
      readonly dueVi: string;
    }
  | { readonly kind: "unclear" };

/** Chữ đứng ngay trước cụm tháng khiến cụm là PHẦN của một ngày/hạn (không phải kỳ): "mùng 1 tháng sau", "đầu tháng sau", "hạn tháng 11". Khoá bỏ dấu. */
const NOT_A_PERIOD_BEFORE = new Set(["ngay", "mung", "mong", "hom", "dau", "giua", "han", "chot", "truoc", "deadline", "nhat", "tu", "den", "toi"]);

/** Chữ (bỏ dấu) nói một HẠN/NGÀY ở phần câu NGOÀI cụm tháng ⇒ hạn do người dùng nói, không suy từ kỳ. */
const DEADLINE_WORDS = new Set(["han", "deadline", "truoc", "ngay", "mung", "mong"]);
/** Ngày dạng số ("5/11", "05-11", "5.11") ở phần câu ngoài cụm tháng. */
const DATE_IN_REST_RE = /(?<!\d)\d{1,2}\s*[/.-]\s*\d{1,2}(?!\d)/u;

/**
 * Chữ nói việc thu CÒN TIẾP sau tháng đó ("tháng 11 trở đi", "đều đặn", "mỗi kỳ", "liên tục", "cứ thế", "thu tiếp", "lặp lại") — khoá bỏ dấu, theo cụm từ
 * liền. Review #1432 M-1: áp ở MỌI trang (trong `readCollectionPeriod`) ⇒ `unclear` ⇒ HỎI hạn, không điền hạn cuối tháng cho một ý định lịch; ở trang LỊCH
 * thì `unclear` = giữ lựa chọn lịch của LLM.
 */
const CONTINUES: readonly (readonly string[])[] = [["tro", "di"], ["deu", "dan"], ["moi", "ky"], ["hang", "ky"], ["lien", "tuc"], ["cu", "the"], ["tiep"], ["lap", "lai"], ["dinh", "ky"]];
/** Câu nói thu còn tiếp sau tháng đó (`CONTINUES`) hoặc có chữ lặp của khuôn mở đợt thu (`hasRecurringMarker` — "hằng tháng", "mỗi tháng"…). */
function mayContinue(text: string): boolean {
  const keys = wordsOf(text).map((w) => w.key);
  return hasRecurringMarker(text) || keys.some((_, i) => CONTINUES.some((c) => c.every((k, j) => keys[i + j] === k)));
}
/**
 * Review #1432 H-2 — ngay sau cụm tháng là nối + một SỐ THÁNG trần ("tháng 11, 12" · "11 và 12" · "11 đến/tới 12" · "11-12" · "11 + 12") ⇒ nhiều tháng,
 * tháng sau bị bỏ lặng nếu đọc là "tháng 11" ⇒ `unclear`. Trên chuỗi bỏ dấu, chữ thường.
 */
const MORE_MONTHS_AFTER_RE = /^\s*(?:,|\+|&|-|–|va|den|toi)\s*(\d{1,2})(?!\d)/u;

/**
 * 🔴 UX-COLLECTION-EXTEND-YEAR M-3 (@UC0196 · B0 đo LLM thật 16/10) — lời NĂM SAU, tập ĐÓNG theo từ (dài trước ngắn sau): "sang năm [sau|tới]" · "năm sau" ·
 * "năm tới". Đây là một NĂM CỤ THỂ (năm hiện tại theo giờ VN + 1, `today` do caller truyền), không phải đoán — khác "tháng 9" lúc tháng 10 (hồi tháng 9 hay
 * tháng 9 năm sau?). Luật dấu như `periodHeadOk` (bỏ dấu thì "nam" ≡ "Nam", "toi" ≡ "tôi"): câu CÓ dấu ⇒ đúng chữ có dấu ("năm", "tới"); câu không dấu
 * ⇒ chữ thường (chữ HOA giữa câu là tên; chữ đầu câu do bàn phím). Khớp theo mặt bỏ dấu mà trượt luật dấu ⇒ KHÔNG phải lời năm nhưng cũng không được
 * im lặng đọc thành năm nay ⇒ `loose` ⇒ `unclear` (HỎI).
 */
const NEXT_YEAR_FORMS: readonly (readonly string[])[] = [["sang", "năm", "sau"], ["sang", "năm", "tới"], ["sang", "năm"], ["năm", "sau"], ["năm", "tới"]];
/**
 * 🔴 Review #1438 HIGH-1 — TỪ ĐƯỢC ĐỨNG NGAY SAU lời năm sau (tập ĐÓNG — chiều thiếu là HỎI, không phải đoán). "sang năm HỌC mới" (năm học) · "sang năm MỚI"
 * (Tết) · "năm sau NỮA" (hai năm?) · "năm sau ĐÂY" không phải "năm VN hiện tại + 1" ⇒ `strict: false` ⇒ HỎI. Được: hết câu · dấu câu (",", ".", …) · chữ
 * mở vế thu/đối tượng ("tháng" của "năm sau tháng 1", "mỗi/mọi/cả/các", "thu/đóng/nộp/góp", "cho", "nhóm/lớp/đội/quỹ") · trợ từ cuối ("nhé/nha/đi/ạ/luôn").
 * Câu có dấu so ĐÚNG chữ có dấu ("mới" ≠ "mỗi"); câu không dấu so mặt bỏ dấu — "moi" (mỗi ≡ mới) chỉ nhận khi từ sau nữa là ĐƠN VỊ NGƯỜI ("moi nguoi/be").
 */
const NEXT_YEAR_FOLLOWERS: readonly string[] = ["tháng", "mỗi", "mọi", "cả", "các", "thu", "đóng", "nộp", "góp", "cho", "nhóm", "lớp", "đội", "quỹ", "nhé", "nha", "nhe", "nhá", "đi", "ạ", "luôn"];
const NEXT_YEAR_FOLLOWER_KEYS: ReadonlySet<string> = new Set(NEXT_YEAR_FOLLOWERS.map((w) => normalizeViText(w)));
/** "moi" không dấu sau lời năm sau = "mỗi" CHỈ khi theo sau là một đơn vị người (không thì có thể là "năm mới"). */
const PER_HEAD_AFTER_MOI: ReadonlySet<string> = new Set(["nguoi", "be", "nha", "ban", "chau", "con", "em", "thanh", "hoc", "ho", "gia", "phu", "thang"]);

interface NextYearMarker {
  readonly start: number;
  readonly end: number;
  /** Đúng luật dấu (xem `NEXT_YEAR_FORMS`) VÀ từ đứng sau thuộc `NEXT_YEAR_FOLLOWERS`. */
  readonly strict: boolean;
  /** Chỉ vế luật dấu (W-1 review HIGH-1): sai dấu ⇒ có thể là tên "Nam tới" — câu KHÔNG có cụm tháng thì không tính là lời về năm. */
  readonly accentOk: boolean;
}
function nextYearMarkers(text: string, unaccentedSentence: boolean): NextYearMarker[] {
  const words = wordsOf(text);
  const out: NextYearMarker[] = [];
  const wordOk = (i: number, want: string): boolean => {
    const w = words[i]!;
    const raw = text.slice(w.start, w.end);
    if (!unaccentedSentence) return w.accented === want;
    return raw === w.key || (i === 0 && raw.toLocaleLowerCase("vi") === w.key);
  };
  const followerOk = (j: number, after: number): boolean => {
    const w = words[j];
    if (w === undefined || /[,.;:!?…]/u.test(text.slice(after, w.start))) return true;
    if (!unaccentedSentence) return NEXT_YEAR_FOLLOWERS.includes(w.accented);
    if (!NEXT_YEAR_FOLLOWER_KEYS.has(w.key)) return false;
    return w.key !== "moi" || PER_HEAD_AFTER_MOI.has(words[j + 1]?.key ?? "");
  };
  for (let i = 0; i < words.length; ) {
    const form = NEXT_YEAR_FORMS.find((f) => f.every((k, j) => words[i + j]?.key === normalizeViText(k)));
    if (form === undefined) {
      i += 1;
      continue;
    }
    const last = words[i + form.length - 1]!;
    const accentOk = form.every((k, j) => wordOk(i + j, k));
    out.push({ start: words[i]!.start, end: last.end, strict: accentOk && followerOk(i + form.length, last.end), accentOk });
    i += form.length;
  }
  return out;
}

/**
 * Review #1438 LOW-2 — đọc lời NĂM SAU quanh cụm tháng `month`, tách khỏi `readCollectionPeriod`: `none` (câu không có lời nào — đường cũ) · `unclear`
 * (có lời mà không đủ điều kiện ⇒ HỎI) · `next` + vùng [cụm tháng ∪ lời năm]. Đủ điều kiện = đúng MỘT lời, `strict`, đứng SÁT cụm tháng (chỉ cách khoảng
 * trắng — "tháng 11 năm sau" · "sang năm tháng 2"). "tháng N" phải TRẦN — kiểm ở `nextYearMonth` (chỗ sở hữu).
 */
type NextYearRead = { readonly kind: "none" } | { readonly kind: "unclear" } | { readonly kind: "next"; readonly start: number; readonly end: number };
function readNextYear(text: string, month: { readonly start: number; readonly end: number }, unaccentedSentence: boolean): NextYearRead {
  const markers = nextYearMarkers(text, unaccentedSentence);
  const marker = markers[0];
  if (marker === undefined) return { kind: "none" };
  const touches = /^\s*$/u.test(marker.start >= month.end ? text.slice(month.end, marker.start) : text.slice(marker.end, month.start));
  if (markers.length > 1 || !marker.strict || !touches) return { kind: "unclear" };
  return { kind: "next", start: Math.min(month.start, marker.start), end: Math.max(month.end, marker.end) };
}

/** Tên đợt thu theo kỳ — "Thu quỹ tháng 11/2026". MỘT khuôn cho tên mặc định của `COL-EQUAL` (`colEqualPlan`) và tên lưới kỳ đặt. */
export function collectionPeriodTitle(periodKey: string): string {
  return `Thu quỹ ${periodKeyLabelVi(periodKey).toLowerCase()}`;
}

/**
 * Tháng đọc theo hướng TỚI. 🔴 QĐ có chủ đích (review #1432 L-3): "tháng N" KHÔNG năm với N < tháng hiện tại ⇒ `unclear` (HỎI), kể cả "tháng 1" gõ lúc
 * tháng 12 — câu đó RẤT có thể là tháng 1 năm sau, nhưng cũng có thể là kể/thu bù kỳ đã qua; một câu hỏi rẻ hơn một hạn sai năm trên đợt thu không sửa
 * được (§4 "thà hỏi"). Người dùng nói "tháng 1 năm sau" (`nextYearMonth`) hay "tháng 1/2027" thì đọc thẳng.
 */
function forwardMonth(r: MonthPhraseRead, today: Ymd): { readonly y: number; readonly m: number } | null {
  if (r.kind === "relative") {
    if (r.which === "past") return null;
    const p = addMonths({ y: today.y, m: today.m, d: 1 }, r.which === "next" ? 1 : 0);
    return { y: p.y, m: p.m };
  }
  if (!Number.isInteger(r.m) || r.m < 1 || r.m > 12) return null;
  const y = r.y ?? today.y;
  return y * 12 + r.m < today.y * 12 + today.m ? null : { y, m: r.m };
}

/** "tháng N" + lời năm sau ⇒ tháng N của năm VN hiện tại + 1 (mọi N hợp lệ — năm sau thì không có kỳ "đã qua"). */
function nextYearMonth(r: MonthPhraseRead, today: Ymd): { readonly y: number; readonly m: number } | null {
  if (r.kind !== "absolute" || r.y !== null || !Number.isInteger(r.m) || r.m < 1 || r.m > 12) return null;
  return { y: today.y + 1, m: r.m };
}

/**
 * 🔴 W-1 UX-COL-OPEN-PERIOD (@UC0195 @UC0196 · nghiệm thu cuối GUIDE-UX2 16/10, LLM thật N-1) — câu KHÔNG có cụm tháng mà vẫn NÓI một kỳ của đợt: cụm
 * khoảng của bảng ngày (`scanDatePeriods` — quý · năm · học kỳ · "đầu năm"), lời NĂM SAU (`nextYearMarkers` — "sang năm", "năm sau/tới", đúng hay trượt
 * từ theo sau vẫn là một lời về năm; sai luật dấu — "Nam tới" — là tên, review HIGH-1), hay cụm KỲ của khuôn mở đợt thu (`collectionTermSpans` — "năm học mới", "năm học 2026-2027", "học kỳ sau", "hk2"). Kỳ nằm
 * TRỌN trong mục đích "tiền <…>" mà khuôn đầu người đọc ra (`parsePerHeadCollection` — "tiền liên hoan cuối năm") là TÊN đợt, in nguyên chữ trên thẻ,
 * không phải lời về hạn ⇒ không tính. Ba bộ đọc là ba bảng đã có — không khuôn thứ tư.
 */
function mentionsOtherTerm(text: string, phrases: readonly DatePeriodPhrase[], unaccentedSentence: boolean): boolean {
  const purpose = parsePerHeadCollection(text)?.purpose ?? null;
  const at = purpose === null ? -1 : text.indexOf(purpose);
  const inPurpose = (r: { readonly start: number; readonly end: number }): boolean => at >= 0 && r.start >= at && r.end <= at + purpose!.length;
  const terms = [...phrases, ...nextYearMarkers(text, unaccentedSentence).filter((m) => m.accentOk), ...collectionTermSpans(text)];
  return terms.some((r) => !inPurpose(r));
}

/** Kỳ THÁNG mà câu mở đợt thu nói — xem docblock đầu file. `null` = câu không nói kỳ nào (đường cũ). Thuần: `today` là ngày ở múi giờ nhóm. */
export function readCollectionPeriod(sentence: string, today: Ymd): CollectionPeriod | null {
  const text = scrubPiiForLlm(sentence).text.normalize("NFC").trim();
  if (text === "") return null;
  const unaccentedSentence = isUnaccentedText(text);
  // Cụm khoảng có chữ "tháng" mà không phải khuôn tháng trần ("đầu tháng" của "đầu tháng sau") vẫn là một lời về tháng ⇒ đếm, và ra `unclear`.
  const allPhrases = scanDatePeriods(text).phrases;
  const months = allPhrases.flatMap((p): { readonly p: DatePeriodPhrase; readonly read: MonthPhraseRead | null }[] => {
    const read = readMonthPhrase(p.text, { unaccentedSentence });
    if (read !== null) return [{ p, read }];
    return wordsOf(p.text).some((w) => w.key === "thang") ? [{ p, read: null }] : [];
  });
  const unclear: CollectionPeriod = { kind: "unclear" };
  if (months.length === 0) return mentionsOtherTerm(text, allPhrases, unaccentedSentence) ? unclear : null;
  const only = months[0]!;
  if (months.length > 1 || only.read === null || only.p.lead !== null || hasNegationOrDoubt(text)) return unclear;
  // UX-COLLECTION-EXTEND-YEAR M-3: lời "năm sau" (`nextYearMarkers`) là NĂM CỤ THỂ CHỈ khi: đúng MỘT lời, đúng luật dấu, không "nữa", đứng SÁT cụm tháng
  // (trước hoặc sau, chỉ cách khoảng trắng — "tháng 11 năm sau" · "sang năm tháng 2"), và cụm tháng là "tháng N" TRẦN (không năm, không "tháng sau").
  // Mọi hình khác ("sang năm thu quỹ tháng 11" — xa cụm tháng · "tháng 11/2027 năm sau" · "năm nay hay năm sau") ⇒ HỎI, không đọc thành năm nay.
  const nextYear = readNextYear(text, only.p, unaccentedSentence);
  if (nextYear.kind === "unclear") return unclear;
  const marker = nextYear.kind === "next" ? nextYear : undefined;
  const span = marker ?? { start: only.p.start, end: only.p.end };
  // Review #1432 H-1: còn BẤT KỲ cụm khoảng nào khác ngoài đúng một cụm tháng (+ lời "năm sau" sát nó) ("quý 4", "năm 2027 năm sau") ⇒ năm/kỳ nào? ⇒ HỎI
  // (§4 không đoán năm nay). Ngoại lệ duy nhất: "năm nay" khi KHÔNG có lời "năm sau" — xác nhận đúng năm mà cách đọc tháng không năm đã dùng.
  const isThisYear = (p: DatePeriodPhrase): boolean => marker === undefined && /^(?:trong\s+)?nam\s+nay$/u.test(normalizeViText(p.text.toLocaleLowerCase("vi")).trim());
  const others = allPhrases.filter((p) => p !== only.p && !(p.start >= span.start && p.end <= span.end) && !isThisYear(p));
  if (others.length > 0) return unclear;
  // H-2: "tháng 11, 12" — nhiều tháng viết tắt.
  const after = MORE_MONTHS_AFTER_RE.exec(normalizeViText(text.slice(span.end).toLocaleLowerCase("vi")));
  if (after !== null && Number(after[1]) >= 1 && Number(after[1]) <= 12) return unclear;
  // M-1: chữ lặp / thu-còn-tiếp ⇒ ý định LỊCH, không phải một đợt hết tháng đó ⇒ HỎI.
  if (mayContinue(text)) return unclear;
  const before = wordsOf(text.slice(0, span.start)).at(-1);
  if (before !== undefined && (/^\d/u.test(before.key) || NOT_A_PERIOD_BEFORE.has(before.key))) return unclear;
  // Review HIGH-2: câu còn nói một HẠN/NGÀY ở chỗ khác ("thu quỹ tháng 11 hạn 5/11 …", "… trước ngày 5") ⇒ hạn là lời người dùng, không phải cuối kỳ ⇒ HỎI.
  const rest = `${text.slice(0, span.start)} ${text.slice(span.end)}`;
  const restWords = wordsOf(rest);
  const latest = restWords.some((w, i) => w.key === "cham" && restWords[i + 1]?.key === "nhat"); // "chậm nhất"
  if (DATE_IN_REST_RE.test(rest) || latest || restWords.some((w) => DEADLINE_WORDS.has(w.key))) return unclear;
  const ym = marker === undefined ? forwardMonth(only.read, today) : nextYearMonth(only.read, today);
  if (ym === null) return unclear;
  const last = addMonths({ y: ym.y, m: ym.m, d: 31 }, 0);
  const dueYmd = formatYmd(last);
  return { kind: "month", periodKey: dueYmd.slice(0, 7), dueYmd, dueVi: `${dueYmd.slice(8, 10)}/${dueYmd.slice(5, 7)}/${dueYmd.slice(0, 4)}` };
}

/** Chữ điền cho một ô; `replacesSpan` ⇒ THAY span Phiếu của ô đó (chỉ ô hạn, khi span là đúng cụm tháng trần của câu). */
export interface CollectionPeriodFill extends CollectionSlotFill {
  readonly replacesSpan?: true;
}

type PeriodPageLike = Pick<AiCapabilityPage, "id" | "collectionFromSentence">;

export interface CollectionPeriodPageInput<P extends PeriodPageLike> {
  readonly sentence: string;
  /** Trang đã được chọn cho lượt này (LLM, hoặc lưới chọn trang trước đó). */
  readonly page: P;
  /** Trang ĐƯỢC PHÉP (cổng vai/cờ — @UC0152). Lưới chỉ chọn trong đây. */
  readonly allowed: readonly P[];
  readonly rejected: string | undefined;
  readonly simulated: boolean;
  readonly today: Ymd;
}

/**
 * LƯỚI CHỌN TRANG (kind `page`): trang đợt MỘT LẦN + câu nói một tháng ⇒ trang `deadline` khai ô hạn (trong `allowed`), hoặc `null` (đường cũ).
 * Luật chung 3: LLM không chạy thật (`rejected`/`simulated`) ⇒ không làm gì.
 */
export function collectionPeriodPageFromSentence<P extends PeriodPageLike>(input: CollectionPeriodPageInput<P>): P | null {
  if (input.rejected !== undefined || input.simulated) return null;
  const cadence = input.page.collectionFromSentence?.cadence;
  if (cadence !== "once" && cadence !== "recurring") return null;
  const period = readCollectionPeriod(input.sentence, input.today);
  if (period === null) return null;
  // Trang LỊCH (COL-RECURRING) — đo LLM thật 15/10 n=6/6: "thu quỹ tháng 11 mỗi người 150k" (cả có dấu lẫn không) ⇒ COL-RECURRING. Một THÁNG nói rõ,
  // không một chữ lặp ⇒ không phải lịch thu mỗi kỳ (lịch sai = đòi tiền cả nhóm MỖI KỲ, nặng hơn một đợt sai). Chỉ chuyển khi tháng đọc ĐƯỢC trọn
  // (`month`) — `readCollectionPeriod` đã trả `unclear` cho câu có chữ lặp/thu-tiếp (review #1432 M-1), cụm khoảng khác (H-1), nhiều tháng (H-2), "từ
  // tháng 11" (ngày bắt đầu lịch) ⇒ giữ trang lịch.
  if (cadence === "recurring" && period.kind !== "month") return null;
  return input.allowed.find((p) => p.collectionFromSentence?.cadence === "deadline" && p.collectionFromSentence.due !== undefined) ?? null;
}

/**
 * Span Phiếu của trang CŨ (đợt một lần) mang sang trang mới khi lưới kỳ chuyển trang (review MEDIUM-1): CHỈ ô cùng id VÀ cùng loại ở cả hai trang
 * và KHÔNG tự điền (COL-EQUAL ↔ COL-DEADLINE: `soTien` "Mỗi người" · `tenDot`) — cùng NGHĨA, chữ đã được neo vào câu. Không có ô đó ở trang mới ⇒ bỏ.
 */
export function collectionPeriodCarriedSpans<S extends InterpreterSpanLike>(
  from: Pick<AiCapabilityPage, "slots">,
  to: Pick<AiCapabilityPage, "slots">,
  spans: readonly S[],
): S[] {
  const slotIn = (p: Pick<AiCapabilityPage, "slots">, id: string) => p.slots.find((x) => x.id === id);
  // Ô TỰ ĐIỀN (nhóm/quỹ) KHÔNG mang: span "vọng" cả câu ở ô đó bị `dropAutofillEchoSpans` lọc trên đường thường — mang sang là vòng qua lọc đó;
  // luật tự điền (nhóm/quỹ duy nhất · nhóm đang chọn) tự lo trên trang mới.
  return spans.filter((sp) => {
    const a = slotIn(from, sp.slot);
    const b = slotIn(to, sp.slot);
    return a !== undefined && b !== undefined && a.type === b.type && b.autofill === undefined;
  });
}

/**
 * LƯỚI ĐIỀN Ô (kind `slot`) trên trang khai `collectionFromSentence.due`: kỳ đọc được ⇒ ô hạn + ô tên; ô tiền Phiếu chưa gạch ⇒ cả câu. Kỳ `unclear` ⇒
 * không điền hạn/tên (trang HỎI hạn). Chỉ ô Phiếu (đã neo, của CHÍNH trang này) chưa gạch.
 */
export function fillCollectionPeriodFromSentence(
  sentence: string,
  anchoredSpans: readonly InterpreterSpanLike[],
  page: Pick<AiCapabilityPage, "collectionFromSentence"> | null,
  today: Ymd,
): CollectionPeriodFill[] {
  const decl = page?.collectionFromSentence;
  if (decl?.due === undefined) return [];
  const period = readCollectionPeriod(sentence, today);
  if (period === null) return [];
  const marked = (slot: string): boolean => anchoredSpans.some((s) => s.slot === slot);
  const fills: CollectionPeriodFill[] = [];
  // Tiền: CHỈ khi câu thuộc khuôn "nghĩa vụ theo đầu người" (`parsePerHeadCollection` — một nguồn với lưới mở đợt thu) ⇒ chữ số "mỗi <người>" của câu
  // (review HIGH-1: "thu quỹ tháng 11 tổng 3 triệu" — tổng hay mỗi người? ⇒ KHÔNG điền, trang HỎI). Kiểu `recurring` không phải đợt có hạn ⇒ không.
  const perHead = marked(decl.amount) ? null : parsePerHeadCollection(sentence);
  if (perHead !== null && perHead.cadence !== "recurring") fills.push({ slot: decl.amount, text: perHead.amount });
  if (period.kind !== "month") return fills;
  // Phiếu gạch ô hạn bằng ĐÚNG cụm tháng của câu ("tháng 11") — bộ đọc ngày không đọc cụm tháng trần (⇒ hỏi lại điều vừa nói) ⇒ THAY span đó bằng
  // ngày cuối của cùng tháng (`replacesSpan`, tiền lệ H08A). Span hạn khác ("5/12", "cuối tuần") là lời người dùng ⇒ giữ.
  const dueSpan = anchoredSpans.find((s) => s.slot === decl.due);
  if (dueSpan === undefined) fills.push({ slot: decl.due, text: period.dueVi });
  else {
    const spanPeriod = readCollectionPeriod(dueSpan.text, today);
    if (spanPeriod?.kind === "month" && spanPeriod.periodKey === period.periodKey) fills.push({ slot: decl.due, text: period.dueVi, replacesSpan: true });
  }
  if (decl.name !== undefined && !marked(decl.name)) fills.push({ slot: decl.name, text: collectionPeriodTitle(period.periodKey) });
  return fills;
}
